/**
 * GuestActivityRoom — the activity a guest joined to weigh in on.
 *
 * Mounted by GuestJoinScreen once `joinAsGuest` succeeds. Everything it reads
 * comes from `get_guest_activity` (migration 740) and everything it writes goes
 * through a token-scoped RPC: a guest holds no session, so a direct table read
 * returns zero rows (`tool_sessions_select`, migration 483, is keyed on
 * `auth.uid()`) and a direct write is refused outright.
 *
 * Two shapes arrive on the same link, because two different session machines
 * mint one:
 *
 *   tool   — a non-group session running a tool activity. Rendered with the
 *            same LiveToolRunner canvas the signed-in participant flow uses,
 *            submitting through `submit_guest_response_with_token`.
 *   group  — a Group Prioritization session (SETUP→OPEN→SCORING→…). No tool
 *            session exists; the guest scores each option against each
 *            criterion and those land in `group_scores` through
 *            `submit_guest_group_score`, where the host's reveal reads them.
 *
 * Per canon 660's guest RPCs a guest may contribute a response and nothing
 * else: this screen never transitions anything.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { LiveToolRunner } from '../../components/prioritization/session/LiveToolRunner';
import type { ToolResultItem } from '../../components/prioritization/ToolWrapper';
import {
  fetchGuestActivity,
  submitGuestResponse,
  submitGuestGroupScores,
  type GuestSession,
} from '../../lib/guestParticipant';
import { AppCard, Eyebrow } from '../components/primitives';
import {
  deriveGuestActivityState,
  parseGuestActivity,
  type GuestActivity,
  type GuestCriterion,
  type GuestOption,
  type GuestPriorScore,
} from './guestActivity';
import { useGuestRoomState, useGuestRoomTimer, type GuestRoomState } from '@/lib/sessions/roomRules';
import { SessionPausedOverlay } from '@/features/sessions-participant/components/SessionPausedOverlay';
import { ItemSpotlightDrawer } from '@/features/sessions-participant/components/ItemSpotlightDrawer';
import { AnnouncementBanner } from '@/features/sessions-participant/components/AnnouncementBanner';
import { RevealOrchestrator } from '@/features/sessions-participant/components/results/RevealOrchestrator';
import { sharedRanks } from '@/lib/sessions/rankTies';
import { RoomCountdown } from '@/components/prioritization/session/RoomCountdown';

interface GuestActivityRoomProps {
  token: string;
  guest: GuestSession;
}

/** How often the room re-asks whether the host has opened the activity. */
const POLL_MS = 8000;

const SCORE_VALUES = [1, 2, 3, 4, 5] as const;

function scoreKey(optionId: string, criteriaId: string): string {
  return `${optionId}::${criteriaId}`;
}

function seedScores(prior: GuestPriorScore[]): Record<string, number> {
  const seeded: Record<string, number> = {};
  for (const s of prior) seeded[scoreKey(s.itemId, s.criteriaId)] = s.score;
  return seeded;
}

function GuestScoringPanel({
  options,
  criteria,
  priorScores,
  onSubmit,
}: {
  options: GuestOption[];
  criteria: GuestCriterion[];
  priorScores: GuestPriorScore[];
  onSubmit: (scores: { item_id: string; criteria_id: string; score: number }[]) => Promise<void>;
}) {
  const [scores, setScores] = useState<Record<string, number>>(() => seedScores(priorScores));
  const [saving, setSaving] = useState(false);

  const total = options.length * criteria.length;
  const done = useMemo(
    () =>
      options.reduce(
        (sum, option) =>
          sum + criteria.filter((c) => scores[scoreKey(option.id, c.id)] !== undefined).length,
        0,
      ),
    [options, criteria, scores],
  );

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(
        Object.entries(scores).map(([key, score]) => {
          const [item_id, criteria_id] = key.split('::');
          return { item_id, criteria_id, score };
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 space-y-4">
      <AppCard>
        <Eyebrow>Score the options</Eyebrow>
        <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
          Rate each option against each criterion, 1 (low) to 5 (high). {done} of {total} scored.
        </p>
      </AppCard>

      {options.map((option) => (
        <AppCard key={option.id}>
          <h3 className="font-display text-base text-gray-900 dark:text-[var(--text-pri)]">
            {option.title}
          </h3>
          {option.description ? (
            <p className="mt-1 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              {option.description}
            </p>
          ) : null}
          {option.url ? (
            <a
              href={option.url}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-1 inline-block font-body text-sm text-slate-blue-600 underline dark:text-slate-blue-300"
            >
              Reference link
            </a>
          ) : null}

          <div className="mt-4 space-y-3">
            {criteria.map((criterion) => {
              const key = scoreKey(option.id, criterion.id);
              return (
                <div key={criterion.id} className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-[140px]">
                    <span className="font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                      {criterion.title}
                    </span>
                    {criterion.description ? (
                      <span className="block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                        {criterion.description}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex gap-1">
                    {SCORE_VALUES.map((value) => {
                      const selected = scores[key] === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          aria-label={`${option.title}: ${criterion.title} = ${value}`}
                          aria-pressed={selected}
                          onClick={() => setScores((prev) => ({ ...prev, [key]: value }))}
                          className={
                            selected
                              ? 'h-9 w-9 rounded-full border border-slate-blue-600 bg-slate-blue-600 font-body text-sm text-white'
                              : 'h-9 w-9 rounded-full border border-gray-300 font-body text-sm text-gray-700 hover:border-slate-blue-400 dark:border-[var(--rule)] dark:text-[var(--text-sec)]'
                          }
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </AppCard>
      ))}

      <button
        type="button"
        disabled={saving || done === 0}
        onClick={() => void handleSubmit()}
        className="w-full rounded-lg bg-slate-blue-600 px-4 py-3 font-body text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? 'Submitting…' : done < total ? `Submit ${done} score${done === 1 ? '' : 's'}` : 'Submit my scores'}
      </button>
    </div>
  );
}

/** The closed result as the reveal shows it: names, totals, tied items sharing a rank. */
function guestRevealItems(room: GuestRoomState): Array<{ rank: number; title: string; score: number }> {
  const result = room.result!;
  const ranks = sharedRanks(result.items.map((i) => i.id), result.ties);
  return result.items.map((i, idx) => ({ rank: ranks[i.id]?.rank ?? idx + 1, title: i.name, score: i.total ?? 0 }));
}

/**
 * The reveal the guest follows. A published session the host never revealed
 * shows in full; otherwise the guest sees exactly what the room sees.
 */
function guestRevealEvents(room: GuestRoomState): GuestRoomState['reveal'] {
  if (room.reveal.length === 0 && room.sessionState === 'PUBLISHED') {
    return [{ event_type: 'reveal_started', payload: {} }, { event_type: 'reveal_complete', payload: {} }];
  }
  return room.reveal;
}

export default function GuestActivityRoom({ token, guest }: GuestActivityRoomProps) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activity, setActivity] = useState<GuestActivity | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<GuestActivity | null> => {
    const result = await fetchGuestActivity(token);
    if (!result.ok) {
      setLoadError(result.error ?? 'Could not load this session.');
      setLoading(false);
      return null;
    }
    const parsed = parseGuestActivity(result.data);
    if (!parsed.ok) {
      setLoadError(parsed.error);
      setLoading(false);
      return null;
    }
    setLoadError(null);
    setActivity(parsed.data);
    setLoading(false);
    return parsed.data;
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    void (async () => {
      const first = await load();
      if (cancelled) return;
      // Realtime is gated by the same RLS that hides these tables from a guest,
      // so a subscription would never fire. Poll instead, and only while there
      // is something to wait for.
      if (first && first.status === 'not_started') {
        timer = setInterval(() => {
          void load();
        }, POLL_MS);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [load]);

  const state = deriveGuestActivityState({ loading, loadError, submitted, activity });
  // S-2: the host's timer, read while the guest is answering.
  const roomTimer = useGuestRoomTimer(token, state.kind === 'ready');
  // S-8: the room's own signals — pause, spotlight, broadcast, the reveal and
  // the closed result — which a guest cannot read as events.
  const room = useGuestRoomState(token, state.kind !== 'loading' && state.kind !== 'error');
  const roomSignals = (
    <>
      <SessionPausedOverlay sessionId={guest.sessionId} paused={room?.paused ?? false} />
      <ItemSpotlightDrawer sessionId={guest.sessionId} current={room?.spotlight ?? null} />
    </>
  );

  if (state.kind === 'loading') {
    return (
      <div className="mt-6 flex items-center justify-center gap-2 py-8 text-gray-500 dark:text-[var(--text-ter)]">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="font-body text-sm">Looking for the activity…</span>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppCard className="mt-6">
        <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
          {state.message}
        </p>
      </AppCard>
    );
  }

  if (state.kind === 'waiting') {
    return (
      <AppCard className="mt-6">
        <Eyebrow>Activity</Eyebrow>
        <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
          Waiting for the host to open the activity. This page updates automatically.
        </p>
      </AppCard>
    );
  }

  if (state.kind === 'closed' || state.kind === 'submitted') {
    return (
      <>
        {roomSignals}
        <AnnouncementBanner sessionId={guest.sessionId} latest={room?.broadcast ?? null} />
        <AppCard className="mt-6">
          {state.kind === 'closed' ? <Eyebrow>Activity</Eyebrow> : null}
          <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
            {state.kind === 'closed'
              ? `This session has closed. Thanks for taking part, ${guest.displayName}.`
              : `Your response has been submitted. Thanks, ${guest.displayName}.`}
          </p>
        </AppCard>
        {room?.result ? (
          <div className="mt-4" data-testid="guest-reveal">
            <RevealOrchestrator sessionId={guest.sessionId} items={guestRevealItems(room)} events={guestRevealEvents(room)} />
          </div>
        ) : null}
      </>
    );
  }

  if (state.kind === 'scoring') {
    return (
      <>
        {submitError ? (
          <AppCard className="mt-6">
            <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
              {submitError}
            </p>
          </AppCard>
        ) : null}
        <GuestScoringPanel
          options={state.options}
          criteria={state.criteria}
          priorScores={state.priorScores}
          onSubmit={async (scores) => {
            setSubmitError(null);
            const result = await submitGuestGroupScores(token, scores);
            if (!result.ok) {
              setSubmitError(result.error ?? 'Could not save your scores.');
              return;
            }
            setSubmitted(true);
          }}
        />
      </>
    );
  }

  // state.kind === 'ready'
  return (
    <div className="mt-6 flex min-h-[420px] flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-[var(--rule)]">
      {roomSignals}
      <AnnouncementBanner sessionId={guest.sessionId} latest={room?.broadcast ?? null} />
      {roomTimer.endsAt !== null ? (
        <div className="flex justify-center border-b border-gray-200 px-4 py-2 dark:border-[var(--rule)]" data-testid="guest-room-timer">
          <RoomCountdown
            endsAt={roomTimer.endsAt}
            atZeroLabel={roomTimer.atZero === 'lock' ? 'Time is up · answers are locked' : 'Time is up'}
          />
        </div>
      ) : null}
      {submitError ? (
        <p className="border-b border-luminous-coral-200 bg-luminous-coral-50 px-4 py-2 font-body text-sm text-luminous-coral-700 dark:border-luminous-coral-900/40 dark:bg-luminous-coral-900/20 dark:text-luminous-coral-300">
          {submitError}
        </p>
      ) : null}
      <LiveToolRunner
        embedded
        sessionId={guest.sessionId}
        toolSessionId={state.toolSessionId}
        workspaceId=""
        selectedToolId={state.toolType}
        items={state.items}
        toolConfig={state.toolConfig}
        sessionReadout={state.readout}
        participantId={guest.guestId}
        participantName={guest.displayName}
        onBack={() => {}}
        onComplete={() => setSubmitted(true)}
        onError={setSubmitError}
        guestPersist={async (results: ToolResultItem[]) => {
          setSubmitError(null);
          const result = await submitGuestResponse(token, state.toolSessionId, results);
          if (!result.ok) {
            throw new Error(result.error ?? 'Could not save your response.');
          }
        }}
      />
    </div>
  );
}
